import { useState, useCallback, useMemo } from "react";
import { useSafeNavigate } from "./useSafeNavigate";
import { isFeatureEnabled } from "../lib/feature-flags";
import { buildPublicIdentifier } from "../lib/identity";

/**
 * Manages state for the avatar-click → VoterModal → FriendProfileModal flow.
 *
 * Returns:
 * - handleAvatarClick(users) — opens VoterModal with the given users
 * - handleUserClick(user) — opens FriendProfileModal directly (skips VoterModal)
 * - modalProps — spread into <ProfileInteractionModals>
 */
export function useProfileInteraction() {
  const safeNavigate = useSafeNavigate();

  const [voterModalOpen, setVoterModalOpen] = useState(false);
  const [voterModalUsers, setVoterModalUsers] = useState([]);
  const [profileUser, setProfileUser] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const handleAvatarClick = useCallback((users) => {
    setVoterModalUsers(users);
    setVoterModalOpen(true);
  }, []);

  const handleViewProfile = useCallback(
    (user) => {
      setVoterModalOpen(false);
      if (isFeatureEnabled("FRIEND_PROFILE_AS_MODAL")) {
        setProfileUser(user);
        setProfileModalOpen(true);
      } else {
        // Route only by the user's CHOSEN public identifier — never fall back
        // to their raw email. If there is no safe identifier, suppress
        // navigation rather than leaking the address into the URL.
        const identifier = buildPublicIdentifier(user);
        if (!identifier) return;
        safeNavigate(`/profile/${encodeURIComponent(identifier)}`);
      }
    },
    [safeNavigate]
  );

  const handleUserClick = useCallback(
    (user) => {
      if (isFeatureEnabled("FRIEND_PROFILE_AS_MODAL")) {
        setProfileUser(user);
        setProfileModalOpen(true);
      } else {
        // Route only by the user's CHOSEN public identifier — never fall back
        // to their raw email. If there is no safe identifier, suppress
        // navigation rather than leaking the address into the URL.
        const identifier = buildPublicIdentifier(user);
        if (!identifier) return;
        safeNavigate(`/profile/${encodeURIComponent(identifier)}`);
      }
    },
    [safeNavigate]
  );

  const modalProps = useMemo(
    () => ({
      voterModalOpen,
      setVoterModalOpen,
      voterModalUsers,
      profileUser,
      profileModalOpen,
      setProfileModalOpen,
      handleViewProfile,
    }),
    [
      voterModalOpen,
      voterModalUsers,
      profileUser,
      profileModalOpen,
      handleViewProfile,
    ]
  );

  return { handleAvatarClick, handleUserClick, modalProps };
}
